import {
  Building2,
  ChartNoAxesCombined,
  ChevronRight,
  Landmark,
  LayoutDashboard,
  Menu,
  ReceiptText,
  Settings,
  ShieldCheck,
  UsersRound,
  X,
  type LucideIcon,
} from "lucide-react";
import { BrandBootShell, SuiteAccountMenu, SuiteLauncher } from "@fulcrum5/suite-ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { navigate, usePathname } from "./app/navigation.ts";
import { platformRoutes, type PlatformRoute, type PlatformRouteSection } from "./routes.tsx";
import { usePlatformSession } from "./session/PlatformSessionProvider.tsx";
import { SignInPage } from "./session/SignInPage.tsx";
import { createPlatformSuiteControls } from "./suite/platformSuiteContext.ts";

const ROUTE_ICONS: Readonly<Record<string, LucideIcon>> = {
  "/": LayoutDashboard,
  "/organizations": Building2,
  "/members": UsersRound,
  "/billing": ReceiptText,
  "/analytics": ChartNoAxesCombined,
  "/settings": Settings,
  "/admin": ShieldCheck,
  "/admin/organizations": Landmark,
  "/admin/platform-admins": ShieldCheck,
};

const SECTION_LABELS: Readonly<Record<PlatformRouteSection, string>> = {
  workspace: "Workspace",
  administration: "Administration",
};

function AppFrame({ children }: { readonly children: ReactNode }): JSX.Element {
  return <div className="platform-app">{children}</div>;
}

function SessionState({ title, detail }: { readonly title: string; readonly detail: string }): JSX.Element {
  return (
    <AppFrame>
      <main className="platform-session-state" aria-live="polite">
        <div className="platform-session-state__mark" aria-hidden="true" />
        <div>
          <h1>{title}</h1>
          <p>{detail}</p>
        </div>
      </main>
    </AppFrame>
  );
}

function NotFound(): JSX.Element {
  return (
    <section className="platform-page platform-page--not-found" aria-labelledby="platform-page-title">
      <p className="platform-page__eyebrow">Platform management</p>
      <h1 id="platform-page-title">Page not found</h1>
      <p className="platform-page__summary">The requested workspace is not available in this account context.</p>
    </section>
  );
}

function routeForPath(routes: readonly PlatformRoute[], pathname: string): PlatformRoute | null {
  return routes.find((route) => route.path === pathname) ?? null;
}

function shouldHandleInternalLink(event: MouseEvent<HTMLAnchorElement>): boolean {
  return !(
    event.defaultPrevented
    || event.button !== 0
    || event.metaKey
    || event.altKey
    || event.ctrlKey
    || event.shiftKey
  );
}

function NavigationSection({
  routes,
  pathname,
  onNavigate,
}: {
  readonly routes: readonly PlatformRoute[];
  readonly pathname: string;
  readonly onNavigate: (event: MouseEvent<HTMLAnchorElement>, path: string) => void;
}): JSX.Element | null {
  if (routes.length === 0) return null;
  const section = routes[0]?.section;
  if (!section) return null;

  return (
    <div className="platform-sidebar__section">
      <p className="platform-sidebar__label">{SECTION_LABELS[section]}</p>
      <nav className="platform-sidebar__nav" aria-label={SECTION_LABELS[section]}>
        {routes.map((route) => {
          const Icon = ROUTE_ICONS[route.path] ?? Building2;
          const selected = route.path === pathname;
          return (
            <a
              className="platform-sidebar__link"
              data-active={selected || undefined}
              href={route.path}
              key={route.path}
              aria-current={selected ? "page" : undefined}
              onClick={(event) => onNavigate(event, route.path)}
            >
              <Icon aria-hidden="true" size={18} strokeWidth={1.75} />
              <span>{route.label}</span>
              {selected ? <ChevronRight aria-hidden="true" size={16} strokeWidth={1.75} /> : null}
            </a>
          );
        })}
      </nav>
    </div>
  );
}

export function App(): JSX.Element {
  const session = usePlatformSession();
  const pathname = usePathname();
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const mobileNavigationTrigger = useRef<HTMLButtonElement>(null);

  const closeMobileNavigation = useCallback((restoreTriggerFocus = false) => {
    setMobileNavigationOpen(false);
    if (restoreTriggerFocus) {
      window.requestAnimationFrame(() => mobileNavigationTrigger.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (!mobileNavigationOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeMobileNavigation(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeMobileNavigation, mobileNavigationOpen]);

  useEffect(() => {
    closeMobileNavigation(false);
  }, [closeMobileNavigation, pathname]);

  const routes = useMemo(() => platformRoutes.filter((route) => (
    !route.requiresF5Admin || session.identity?.isF5Admin === true
  )), [session.identity?.isF5Admin]);
  const workspaceRoutes = useMemo(() => routes.filter((route) => route.section === "workspace"), [routes]);
  const administrationRoutes = useMemo(() => routes.filter((route) => route.section === "administration"), [routes]);
  const selectedRoute = routeForPath(routes, pathname);
  const suiteControls = useMemo(() => createPlatformSuiteControls(session), [session]);

  const onNavigate = useCallback((event: MouseEvent<HTMLAnchorElement>, path: string) => {
    if (!shouldHandleInternalLink(event)) return;
    event.preventDefault();
    navigate(path);
  }, []);

  // Unauthenticated: SignInPage is the only brand hero (no ProductSplash → SignIn double soft-blur).
  if (session.phase === "unauthenticated") {
    return <SignInPage />;
  }

  const bootReady = session.phase !== "loading";

  let body: ReactNode;
  if (session.phase === "loading") {
    body = null;
  } else if (session.phase === "error") {
    body = <SessionState title="Platform is unavailable" detail={session.message ?? "Please try again shortly."} />;
  } else if (session.phase === "access-pending") {
    body = <SessionState title="Organization access pending" detail="An active organization is required before this workspace can open." />;
  } else {
    body = (
      <AppFrame>
        <header className="platform-topbar">
          <div className="platform-topbar__brand-cluster">
            {suiteControls ? (
              <SuiteLauncher
                className="platform-topbar__launcher"
                context={suiteControls.context}
                onSwitchOrganization={suiteControls.switchOrganization}
              />
            ) : null}
            <a className="platform-brand" href="/" onClick={(event) => onNavigate(event, "/")}>
              <span className="platform-brand__mark" aria-hidden="true">5</span>
              <span className="platform-brand__name">Fulcrum5</span>
              <span className="platform-brand__product">Platform</span>
            </a>
          </div>
          <div className="platform-topbar__actions">
            {suiteControls ? (
              <SuiteAccountMenu
                className="platform-topbar__account"
                context={suiteControls.context}
                onLogout={suiteControls.logout}
              />
            ) : null}
            <button
              aria-controls="platform-sidebar"
              aria-expanded={mobileNavigationOpen}
              aria-label={mobileNavigationOpen ? "Close navigation" : "Open navigation"}
              className="platform-icon-button platform-mobile-nav-trigger"
              onClick={() => setMobileNavigationOpen((open) => !open)}
              ref={mobileNavigationTrigger}
              type="button"
            >
              {mobileNavigationOpen ? <X aria-hidden="true" size={19} /> : <Menu aria-hidden="true" size={19} />}
            </button>
          </div>
        </header>
        <button
          aria-label="Close navigation"
          className="platform-sidebar-backdrop"
          data-open={mobileNavigationOpen || undefined}
          onClick={() => closeMobileNavigation(true)}
          tabIndex={mobileNavigationOpen ? 0 : -1}
          type="button"
        />
        <aside
          aria-label="Platform navigation"
          className="platform-sidebar"
          data-open={mobileNavigationOpen || undefined}
          id="platform-sidebar"
        >
          <NavigationSection onNavigate={onNavigate} pathname={pathname} routes={workspaceRoutes} />
          <NavigationSection onNavigate={onNavigate} pathname={pathname} routes={administrationRoutes} />
        </aside>
        <main className="platform-main" id="platform-main">{selectedRoute?.element ?? <NotFound />}</main>
      </AppFrame>
    );
  }

  return (
    <BrandBootShell
      holdLabel="Opening Platform"
      productName="Platform"
      ready={bootReady}
    >
      {body}
    </BrandBootShell>
  );
}
